import { useState } from "react";
import { C, eras, perks, studies } from "@bd/content";
import { BALANCE, houseMechanic, perkState, researchCap, researchOptions, researchWeeks, type Branch, type Company, type ResearchOption } from "@bd/sim";
import { Icon, Sheet, Tabs, Why } from "@bd/ui-kit";
import { me, useGame } from "../store";
import { researchLines } from "../lib/why";
import { t, tp } from "../i18n";

/** The list is grouped by kind, so each card doesn't repeat it. */
const KIND: Record<string, string> = { st: "Studies", m: "Mechanics", t: "Themes", a: "Audiences", f: "Formats", c: "Components", ch: "Channels", fa: "Factories", p: "Testers" };
const BRANCHES: { id: Branch; label: string; blurb: string }[] = [
  { id: "mechanics", label: "Ideas", blurb: "New mechanics and themes to design with." },
  { id: "components", label: "Components", blurb: "Better parts, new formats, cheaper and cleaner printing." },
  { id: "business", label: "Business", blurb: "Channels, factories, audiences, loans, marketing and licensing." },
  { id: "studio", label: "Studio", blurb: "Perks for your people, pets, plants and playtesters." },
];

/** A study's text in the player's language (the same filling-in as studyDesc in the rules engine). */
function studyText(co: Company, id: string): string {
  const st = studies.find((x) => `st:${x.id}` === id);
  if (!st) return "";
  const m = houseMechanic(co);
  return t(st.desc, { n: st.reveals, mechanic: m ? t(C.mechanic[m].name) : t("your main mechanic") });
}

/** Who likes a theme, if your notebook knows (it fills in as you test and release). */
function fansOf(o: ResearchOption, co: Company): string[] {
  const theme = o.id.replace(/^t:/, "");
  return Object.keys(C.audience).filter((a) => (co.notebook[`ta:${theme}:${a}`] ?? 0) >= 4).map((a) => t(C.audience[a].name));
}

function detail(o: ResearchOption, co: Company): string {
  if (o.kind === "pk") return t(C.perk[o.id.slice(3)]?.desc ?? "");
  if (o.kind === "st") return studyText(co, o.id);
  if (o.kind === "t") {
    const fans = fansOf(o, co);
    return fans.length ? t("Liked by {list}", { list: fans.join(", ") }) : "";
  }
  return "";
}

/** A cost button's face: the research flask and the number (the word is on hover). */
function Cost({ n }: { n: number }) {
  return <><Icon name="research" size={14} style={{ verticalAlign: -2, marginRight: 4 }} />{n}</>;
}

/** Time, as a small clock (the ui-kit has no clock icon yet). */
export function Clock({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden style={{ flex: "none" }}>
      <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
    </svg>
  );
}

/** Weeks with a clock: "◷ 13". */
function Weeks({ n }: { n: number }) {
  return <span className="biz-ic num muted" title={tp(n, "{n} week", "{n} weeks")}><Clock />{n}</span>;
}

function nameOf(id: string): string {
  if (id.startsWith("pk:")) return t(C.perk[id.slice(3)]?.name ?? id);
  if (id.startsWith("st:")) return t(studies.find((x) => `st:${x.id}` === id)?.name ?? id);
  const [kind, key] = id.split(":");
  const table: Record<string, Record<string, { name: string }>> = { m: C.mechanic, t: C.theme, a: C.audience, f: C.format, c: C.component, ch: C.channel, fa: C.factory, p: C.pool };
  return t(table[kind]?.[key]?.name ?? id);
}

/** Why a perk is locked or ruled out, in the player's language (the same reasons perkState gives). */
function lockedWhy(co: Company, perkId: string, state: string): string | null {
  const p = perks.find((x) => x.id === perkId);
  if (!p) return "";
  const name = (id: string) => t(perks.find((x) => x.id === id)?.name ?? id);
  if (state === "ruled_out") {
    const taken = [...co.researched, ...(co.researching ?? []).map((r) => r.id.slice(3))];
    const rival = (p.excludes ?? []).find((x) => taken.includes(x));
    return rival ? t("You chose {perk}", { perk: name(rival) }) : "";
  }
  const need = (p.requires ?? []).filter((x) => !co.researched.includes(x));
  if (need.length) return t("Needs {list}", { list: need.map(name).join(t(" and ")) });
  // only its era holds it back, and the era heading above already says the year
  return null;
}

export function Research() {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const cmd = useGame((s) => s.cmd);
  const toast = useGame((s) => s.toast);
  const co = me(game)!;
  const [branch, setBranch] = useState<Branch>("mechanics");
  const [open, setOpen] = useState<string | null>(null);
  const opts = researchOptions(co, game.week);
  const busy = co.researching ?? [];
  const slots = BALANCE.researchSlots[co.level] ?? 1;
  const free = busy.length < slots;
  const count = (b: Branch) => opts.filter((o) => o.branch === b && o.cost <= co.research).length;
  // perks show as a tree (every perk in the branch, with why it's locked); other items as a list of what's on offer
  const list = opts.filter((o) => o.branch === branch && o.kind !== "pk");
  const groups = [...new Set(list.map((o) => o.kind))].map((kind) => ({ kind, items: list.filter((o) => o.kind === kind) }));
  const tree = perks.filter((p) => p.branch === branch);
  const start = async (o: ResearchOption) => {
    const r = await cmd({ type: "research", id: o.id });
    if (!r.ok) toast({ title: t("Not yet"), text: r.error ? t(r.error) : r.error, tone: "warn" });
    else toast({ title: t("Researching {name}", { name: t(o.name) }), text: tp(researchWeeks(o), "Ready in about {n} week.", "Ready in about {n} weeks."), tone: "good" });
  };
  const optFor = (perkId: string) => opts.find((o) => o.id === `pk:${perkId}`);
  const lines = [...researchLines(co).map((l) => ({ ...l, label: t(l.label), note: l.note ? t(l.note) : l.note })), { label: t("Most you can bank"), value: researchCap(co, game.week) }];
  // tap a card to read what it does (hover shows it too)
  const toggle = (id: string) => setOpen((v) => (v === id ? null : id));
  const title = (
    <span className="biz-title">
      {t("Research")}
      <span className="biz-chip" title={t("Research points")}>
        <Icon name="research" size={14} color="var(--teal)" />{Math.floor(co.research)}
        <Why title={t("Research points")} lines={lines} note={t("Research slots: one in a garage or office, two in a studio, three in HQ. Points over the bank limit speed up current research; with nothing running, they stop growing.")} />
      </span>
      <span className="biz-chip" title={t("Slots {busy}/{slots}", { busy: busy.length, slots })} aria-label={t("Slots {busy}/{slots}", { busy: busy.length, slots })}>
        {Array.from({ length: slots }, (_, i) => <span key={i} className={`biz-pip${i < busy.length ? " on" : ""}`} />)}
      </span>
    </span>
  );
  return (
    <Sheet title={title} onClose={() => set({ screen: null })} wide testId="research">
      {busy.length > 0 && (
        <div className="stack" style={{ gap: 6, marginBottom: 10 }} data-testid="research-busy">
          {busy.map((r, i) => (
            <div key={`${r.id}:${i}`} className="card spread" style={{ padding: "8px 12px" }}>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span className="row" style={{ gap: 8 }}><strong className="ellipsis">{nameOf(r.id)}</strong><Weeks n={Math.max(1, Math.ceil(r.left))} /></span>
                <span className="meter" style={{ display: "block", height: 4, marginTop: 6, background: "var(--paper-2)", borderRadius: 2 }}><span style={{ display: "block", height: 4, width: `${Math.round((1 - r.left / r.weeks) * 100)}%`, background: "var(--teal)", borderRadius: 2 }} /></span>
              </span>
              <button className="btn small ghost" onClick={() => void cmd({ type: "cancelResearch", id: r.id })} title={t("Points are not refunded.")}>{t("Stop")}</button>
            </div>
          ))}
        </div>
      )}
      <div className="biz-tabs"><Tabs tabs={BRANCHES.map((b) => ({ id: b.id, label: count(b.id) ? `${t(b.label)} · ${count(b.id)}` : t(b.label) }))} value={branch} onChange={setBranch} /></div>
      <div style={{ height: 10 }} />
      {list.length > 0 && (
        <div className="stack" style={{ gap: 10 }} data-testid="research-list">
          {groups.map((g) => (
            <div key={g.kind} className="stack" style={{ gap: 6 }}>
              {KIND[g.kind] && <span className="caps">{t(KIND[g.kind])}</span>}
              <div className="grid2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
                {g.items.map((o) => {
                  const d = detail(o, co);
                  // a theme's audience is a fact to choose by: it stays on the card; a study's how-to opens on tap
                  const liked = o.kind === "t" && d;
                  return (
                    <div key={o.id} className="card stack biz-opt" style={{ gap: 4 }} title={d || undefined} onClick={() => !liked && d && toggle(o.id)} data-testid="research-option">
                      <div className="spread">
                        <strong className="ellipsis">{t(o.name)}{o.kind === "st" && <span className="muted" style={{ marginLeft: 6 }} title={t("Repeatable")}>↻</span>}{o.ahead && <span className="badge" style={{ marginLeft: 6 }} title={t("Ahead of its time: triple cost")}>×3</span>}</strong>
                        <span className="row" style={{ gap: 10, flex: "none" }}>
                          <Weeks n={researchWeeks(o)} />
                          <button className="btn small" disabled={co.research < o.cost || !free} onClick={(e) => { e.stopPropagation(); void start(o); }} title={tp(o.cost, "{n} point", "{n} points")} aria-label={tp(o.cost, "{n} point", "{n} points")} data-testid="research-buy"><Cost n={o.cost} /></button>
                        </span>
                      </div>
                      {liked && <span className="biz-line"><span className="biz-ic"><Icon name="fans" size={13} />{fansOf(o, co).join(", ")}</span></span>}
                      {!liked && d && open === o.id && <span className="desc">{d}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {tree.length > 0 && (
        <div className="stack" style={{ gap: 10, marginTop: list.length ? 14 : 0 }} data-testid="perk-tree">
          <span className="caps">{t("Perks")}</span>
          {eras.filter((e) => tree.some((p) => p.era === e.id)).map((e) => (
            <div key={e.id} className="stack" style={{ gap: 6 }}>
              <span className="muted" style={{ fontSize: "0.8rem" }}>{t(e.name)} · {e.from}</span>
              <div className="grid2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
                {tree.filter((p) => p.era === e.id).map((p) => {
                  const st = perkState(co, p.id, game.week);
                  const o = optFor(p.id);
                  const either = (p.excludes ?? []).map((x) => C.perk[x]?.name).filter(Boolean).map((n) => t(n));
                  const shut = st.state === "locked" || st.state === "ruled_out";
                  const why = shut && st.why ? lockedWhy(co, p.id, st.state) : null;
                  return (
                    <div key={p.id} className="card stack biz-opt" title={t(p.desc)} onClick={() => toggle(p.id)} style={{ gap: 4, opacity: shut ? 0.6 : 1, borderColor: st.state === "done" ? "var(--green)" : undefined }} data-testid="perk-node">
                      <div className="spread">
                        <strong className="ellipsis">{t(p.name)}</strong>
                        {st.state === "done" ? <span className="good biz-ic" title={t("Yours")}><Icon name="check" size={16} /></span>
                          : st.state === "researching" ? <span className="biz-ic" style={{ color: "var(--teal)" }} title={t("Researching")}><Clock size={15} /></span>
                          : o ? <button className="btn small" disabled={co.research < o.cost || !free} onClick={(e) => { e.stopPropagation(); void start(o); }} title={tp(o.cost, "{n} point", "{n} points")} aria-label={tp(o.cost, "{n} point", "{n} points")}><Cost n={o.cost} /></button>
                          : shut ? <Icon name="lock" size={14} color="var(--muted)" />
                          : null}
                      </div>
                      {open === p.id && <span className="desc">{t(p.desc)}</span>}
                      {either.length > 0 && st.state !== "done" && st.state !== "ruled_out" && <span className="biz-ic" style={{ fontSize: "0.8rem", color: "var(--warn)" }} title={t("Not with {list}", { list: either.join(", ") })}>⇄ {either.join(", ")}</span>}
                      {why !== null && <span className="muted" style={{ fontSize: "0.8rem" }}>{why || t(st.why!)}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {list.length === 0 && tree.length === 0 && <p className="muted">{t("Nothing new.")}</p>}
    </Sheet>
  );
}
